import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Star, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "@/lib/toast";
import { useAuth } from "../lib/auth";
import GoogleLoginButton from "../components/GoogleLoginButton";
import SEOHead from "../components/SEOHead";

function safeReturnPath(value) {
  return value && value.startsWith("/") && !value.startsWith("//") ? value : "/app";
}

export default function Login() {
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(safeReturnPath(searchParams.get("return")));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign in");
    } finally {
      setBusy(false);
    }
  };

  // Someone with a live session landing here should just be in the app — unless
  // they were sent with somewhere to return to. An invitation addressed to a
  // different email links here precisely so its recipient can sign in as that
  // account; redirecting would leave them no way to switch, since the page they
  // came from would only tell them to sign in again.
  if (!authLoading && user && !searchParams.get("return")) {
    return <Navigate to="/app" replace />;
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAF6EE] text-[#2B2320]">
      <SEOHead
        title="Sign in to Polaris"
        description="Sign in to your Polaris Travel CRM workspace. Access your clients, trips, pipeline, and Copilot — or sign in with Google in one click."
        path="/login"
        noIndex={true}
      />
      <div className="flex flex-col justify-between p-10 lg:p-14">
        <a href="https://polariscrm.ai" className="flex items-center gap-2.5" data-testid="login-brand-mark">
          <div className="w-9 h-9 rounded-xl bg-[#2B2320] text-white grid place-items-center">
            <Star className="w-4 h-4 fill-white" strokeWidth={0} />
          </div>
          <div>
            <div className="font-display text-body font-semibold leading-none">Polaris</div>
            <div className="text-caption uppercase tracking-[0.22em] text-[#A69C92] mt-1">Travel CRM</div>
          </div>
        </a>

        <div className="max-w-md mx-auto w-full">
          <div className="text-caption uppercase tracking-[0.3em] text-[#8B827A] mb-3">Sign in</div>
          <h1 className="font-display text-h1 leading-[1.05] font-medium">
            Welcome <span className="italic text-[#C96F53]">back.</span>
          </h1>
          <p className="mt-3 text-[#6B6158]">Pick up where your clients left off.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="login-email-input"
                className="h-11 rounded-xl bg-white border-[#E8DFD1] focus-visible:ring-[#C96F53]/40"
                placeholder="you@agency.com"
              />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-xs font-medium text-[#C96F53] underline-offset-4 hover:underline"
                  data-testid="login-forgot-password"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="h-11 rounded-xl bg-white border-[#E8DFD1] focus-visible:ring-[#C96F53]/40"
                placeholder="••••••••"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full h-11 rounded-full bg-[#C96F53] hover:bg-[#B5604A] text-white"
            >
              {busy ? "Signing in…" : (<>Sign in <ArrowRight className="w-4 h-4 ml-2" /></>)}
            </Button>
          </form>

          {process.env.REACT_APP_GOOGLE_CLIENT_ID && (
            <>
              <div className="mt-6 flex items-center gap-3">
                <div className="h-px flex-1 bg-[#E8DFD1]" />
                <span className="text-xs text-[#A69C92]">or continue with</span>
                <div className="h-px flex-1 bg-[#E8DFD1]" />
              </div>
              <div className="mt-4">
                <GoogleLoginButton />
              </div>
            </>
          )}

          <p className="mt-8 text-sm text-[#6B6158]">
            New here?{" "}
            <Link
              to={`/register${searchParams.get("return") ? `?return=${encodeURIComponent(searchParams.get("return"))}` : ""}`}
              className="text-[#2B2320] font-medium underline-offset-4 hover:underline"
              data-testid="login-to-register"
            >
              Create a workspace
            </Link>
          </p>
        </div>

        <div className="text-xs text-[#A69C92]">© {new Date().getFullYear()} Polaris</div>
      </div>

      <div className="hidden lg:block relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/16116486/pexels-photo-16116486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1100&w=1200"
          alt="A coastal village at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2B2320]/10 via-transparent to-[#2B2320]/40" />
        <div className="relative h-full flex items-end p-10">
          <div className="text-white max-w-md">
            <div className="text-caption uppercase tracking-[0.3em] opacity-80 mb-3">Positano · April</div>
            <div className="font-display text-3xl leading-tight">
              "A CRM that finally matches <span className="italic">the craft</span> of the trips we build."
            </div>
            <div className="mt-5 text-sm opacity-85">Olivia Harrington — Founder, North &amp; Nomad</div>
          </div>
        </div>
      </div>
    </div>
  );
}
